import {
  LIST_ADDONS,
  OrderByInput,
  useAddOnListOrder,
  useSetAddOnListOrder,
} from '@gripp/shared-logic';
import { InfiniteDataGrid, SelectionStatus } from '@gripp/shared-ui';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, useWindowDimensions, View } from 'react-native';
import { useMaxHeight } from '../../shared/hooks/useMaxHeight';
import { CreateAddonButton } from './createAddonButton';
import { EditAddonButton } from './editAddonButton';
import { useColumnsAddOn } from './useColumnsAddOn';

export type SelectedAddonRow = {
  id: string;
};

type AddOnsListProps = {
  filter: any;
  showCheckboxColumn: boolean;
  onRowsSelectedChange?: (rows: SelectedAddonRow[]) => void;
  selectedRowIds?: string[];
  selectionStatus?: SelectionStatus;
  onSelectionStatusChange?: (status: SelectionStatus) => void;
};

export const AddOnsList = ({
  filter,
  showCheckboxColumn,
  onRowsSelectedChange,
  selectedRowIds,
  selectionStatus,
  onSelectionStatusChange,
}: AddOnsListProps) => {
  const { t } = useTranslation();
  const { containerMaxHeight, listMaxHeight } = useMaxHeight();
  const { width } = useWindowDimensions();

  const { data: addOnOrder } = useAddOnListOrder();
  const { mutateAsync: setAddOnOrder } = useSetAddOnListOrder();

  const columns = useColumnsAddOn(showCheckboxColumn);

  // Legacy internal selection state — only used when the caller does not
  // provide `onRowsSelectedChange` (i.e. the pre-Crop-Circle route).
  const useLegacyHeader = !onRowsSelectedChange;
  const [legacySelectedIds, setLegacySelectedIds] = useState<string[]>([]);

  const onOrderByChange = async (orderBy: OrderByInput) => {
    await setAddOnOrder(orderBy);
  };

  const updateRowsSelected = (rows: any[]) => {
    if (onRowsSelectedChange) {
      onRowsSelectedChange(rows.map((row) => ({ id: row.id })));
    } else {
      setLegacySelectedIds(rows.map((row) => row.id));
    }
  };

  const legacyHeaderContent = useMemo(() => {
    if (!useLegacyHeader) return undefined;
    if (legacySelectedIds.length === 1) {
      return (
        <EditAddonButton
          addonId={legacySelectedIds[0]}
          label={t('addOn.edit.label')}
          onClose={() => setLegacySelectedIds([])}
        />
      );
    }
    return <CreateAddonButton />;
  }, [useLegacyHeader, legacySelectedIds, t]);

  const selectedIdsSet = useMemo(
    () => new Set(selectedRowIds ?? []),
    [selectedRowIds]
  );
  const isRowSelected = useCallback(
    (row: { id?: string }) => (row?.id ? selectedIdsSet.has(row.id) : false),
    [selectedIdsSet]
  );

  const renderEmpty = () => (
    <View style={styles.emptyContainer}>
      <View style={[styles.textWrapper, { maxWidth: width - 10 }]}>
        <Text style={styles.emptyTapText} numberOfLines={2}>
          {t('addOn.textEmpty')}
        </Text>
      </View>
    </View>
  );

  return (
    <View
      style={[
        styles.addonContainer,
        {
          maxHeight: containerMaxHeight,
        },
      ]}
    >
      <InfiniteDataGrid
        columns={columns}
        query={LIST_ADDONS}
        dataPrefix="addon.listAddons"
        modelName="addon"
        orderBy={addOnOrder}
        pageSize={50}
        filter={filter}
        hideHeader={false}
        listMaxHeight={listMaxHeight}
        paddingBottom={0}
        showScrollIndicator={true}
        onOrderByChange={onOrderByChange}
        textEmpty={renderEmpty()}
        showEmptyTable
        updateRowsSelected={updateRowsSelected}
        isRowSelected={onRowsSelectedChange ? isRowSelected : undefined}
        selectionStatus={selectionStatus}
        onSelectionStatusChange={onSelectionStatusChange}
        headerContent={legacyHeaderContent}
        exportFileName="addons-export"
      />
    </View>
  );
};

const styles = StyleSheet.create({
  addonContainer: {
    flex: 1,
    borderRadius: 8,
    overflow: 'hidden',
    zIndex: -1,
  },
  emptyContainer: {
    flexDirection: 'column',
    rowGap: 40,
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
    width: '100%',
  },
  textWrapper: {
    width: '100%',
  },
  emptyTapText: {
    fontWeight: '700',
    fontSize: 22,
    textAlign: 'center',
  },
});
